import { useCallback, useEffect, useMemo } from "react";
import { useCookies } from "react-cookie";

import { SPACELIFT_LOGINS_COOKIE } from "constants/cookies";
import {
  AllowedRegion,
  createAccountNameWithRegion,
  getRegion,
  isAccountNameValid,
} from "utils/region";

const getCookieParts = () => {
  const hostnameParts = window.location.hostname.split(".");
  const domain = `.${hostnameParts.slice(-2).join(".")}`;

  const expiryDate = new Date();
  expiryDate.setMonth(expiryDate.getMonth() + 1);

  return {
    expiryDate,
    domain,
    hostnameParts,
  };
};

const useSpaceliftLoginsCookie = (shouldSetCookieLogin = false) => {
  const [cookies, setCookie] = useCookies([SPACELIFT_LOGINS_COOKIE]);

  const cookieLogins: string[] = useMemo(
    () =>
      (cookies[SPACELIFT_LOGINS_COOKIE] ? cookies[SPACELIFT_LOGINS_COOKIE].split(",") : []).filter(
        isAccountNameValid
      ),
    [cookies]
  );

  const setCookieLogins = useCallback(
    (newCookieLogins: string[]) => {
      const { expiryDate, domain } = getCookieParts();

      setCookie(SPACELIFT_LOGINS_COOKIE, newCookieLogins.filter(isAccountNameValid).join(","), {
        domain,
        expires: expiryDate,
      });
    },
    [setCookie]
  );

  useEffect(() => {
    if (!shouldSetCookieLogin) {
      return;
    }

    const { expiryDate, domain, hostnameParts } = getCookieParts();
    const currentLogin = createAccountNameWithRegion(hostnameParts[0], getRegion());

    if (cookieLogins.includes(currentLogin)) {
      return;
    }

    const newCookieLogins = [...cookieLogins, currentLogin].filter(isAccountNameValid);


    setCookie(SPACELIFT_LOGINS_COOKIE, newCookieLogins.join(","), {
      domain,
      expires: expiryDate,
    });
  }, [cookieLogins, setCookie, shouldSetCookieLogin]);

  const cookieLoginsWithRegion = useMemo(() => {
    return cookieLogins.map((item: string) => {
      const [accountName, region] = item.split(".");

      return {
        accountName,
        region: region as AllowedRegion,
      };
    });
  }, [cookieLogins]);

  return { cookieLoginsWithRegion, setCookieLogins };
};

export default useSpaceliftLoginsCookie;
